/**
 * D3 Graph Utilities - Pure functions for graph visualization
 */
import * as d3 from 'd3';

// ============================================================================
// CONSTANTS
// ============================================================================

export const D3_NODE_TYPES = {
  ORG: 'org',
  ASSET: 'asset',
  TECH: 'tech',
  CVE: 'cve',
  CWE: 'cwe',
  CAPEC: 'capec',
  TTP: 'ttp',
  APT: 'apt',
};

// Light theme colors
export const LIGHT_THEME = {
  background: '#fafbfc',
  border: '#e9ecef',
  text: '#333',
  textMuted: '#666',
  linkOpacity: 0.35,
  nodeColors: {
    [D3_NODE_TYPES.ORG]: '#6366f1',
    [D3_NODE_TYPES.ASSET]: '#10b981',
    [D3_NODE_TYPES.TECH]: '#f97316',
    [D3_NODE_TYPES.CVE]: '#e53935',
    [D3_NODE_TYPES.CWE]: '#9c27b0',
    [D3_NODE_TYPES.CAPEC]: '#00897b',
    [D3_NODE_TYPES.TTP]: '#1e88e5',
    [D3_NODE_TYPES.APT]: '#f59e0b',
  },
};

// Dark theme colors
export const DARK_THEME = {
  background: '#0a0a1a',
  backgroundGradient: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a1a 100%)',
  border: 'rgba(255, 255, 255, 0.1)',
  text: '#fff',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  linkOpacity: 0.3,
  nodeColors: {
    [D3_NODE_TYPES.ORG]: '#00f5ff',
    [D3_NODE_TYPES.ASSET]: '#34d399',
    [D3_NODE_TYPES.TECH]: '#fb923c',
    [D3_NODE_TYPES.CVE]: '#ffcc00',
    [D3_NODE_TYPES.CWE]: '#e040fb',
    [D3_NODE_TYPES.CAPEC]: '#1de9b6',
    [D3_NODE_TYPES.TTP]: '#ff00aa',
    [D3_NODE_TYPES.APT]: '#ff3355',
  },
};

/**
 * Get theme colors based on dark mode flag
 * Reads node colors from CSS custom properties (set by the active palette)
 * Falls back to hardcoded theme colors if CSS vars are not available
 */
export const getThemeFromCSS = (isDark = false) => {
  const theme = isDark ? DARK_THEME : LIGHT_THEME;
  const style = getComputedStyle(document.documentElement);
  const get = name => style.getPropertyValue(name).trim();

  // Read palette colors from CSS variables (set by applyPaletteToCSS)
  const nodeColors = {
    [D3_NODE_TYPES.ORG]: get('--node-org') || theme.nodeColors[D3_NODE_TYPES.ORG],
    [D3_NODE_TYPES.ASSET]: get('--node-asset') || theme.nodeColors[D3_NODE_TYPES.ASSET],
    [D3_NODE_TYPES.TECH]: get('--node-tech') || theme.nodeColors[D3_NODE_TYPES.TECH],
    [D3_NODE_TYPES.CVE]: get('--node-cve') || theme.nodeColors[D3_NODE_TYPES.CVE],
    [D3_NODE_TYPES.CWE]: get('--node-cwe') || theme.nodeColors[D3_NODE_TYPES.CWE],
    [D3_NODE_TYPES.CAPEC]: get('--node-capec') || theme.nodeColors[D3_NODE_TYPES.CAPEC],
    [D3_NODE_TYPES.TTP]: get('--node-ttp') || theme.nodeColors[D3_NODE_TYPES.TTP],
    [D3_NODE_TYPES.APT]: get('--node-apt') || theme.nodeColors[D3_NODE_TYPES.APT],
  };

  return {
    background: theme.background,
    border: theme.border,
    text: theme.text,
    textMuted: theme.textMuted,
    linkOpacity: theme.linkOpacity,
    nodeColors,
  };
};

// MDI icon paths for each node type (eslint-disable max-len)
/* eslint-disable max-len */
export const NODE_ICONS = {
  [D3_NODE_TYPES.ORG]: 'M12,3L2,12H5V20H19V12H22L12,3M12,8.75A2.25,2.25 0 0,1 14.25,11A2.25,2.25 0 0,1 12,13.25A2.25,2.25 0 0,1 9.75,11A2.25,2.25 0 0,1 12,8.75M12,15C13.5,15 16.5,15.75 16.5,17.25V18H7.5V17.25C7.5,15.75 10.5,15 12,15Z',
  [D3_NODE_TYPES.ASSET]: 'M4,1H20A1,1 0 0,1 21,2V6A1,1 0 0,1 20,7H4A1,1 0 0,1 3,6V2A1,1 0 0,1 4,1M4,9H20A1,1 0 0,1 21,10V14A1,1 0 0,1 20,15H4A1,1 0 0,1 3,14V10A1,1 0 0,1 4,9M4,17H20A1,1 0 0,1 21,18V22A1,1 0 0,1 20,23H4A1,1 0 0,1 3,22V18A1,1 0 0,1 4,17M9,5H10V3H9V5M9,13H10V11H9V13M9,21H10V19H9V21M5,3V5H7V3H5M5,11V13H7V11H5M5,19V21H7V19H5Z',
  [D3_NODE_TYPES.TECH]: 'M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L6.04,7.5L12,10.85L17.96,7.5L12,4.15Z',
  [D3_NODE_TYPES.CVE]: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z',
  [D3_NODE_TYPES.CWE]: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1Z',
  [D3_NODE_TYPES.CAPEC]: 'M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6L16,10H13V14H11V10H8L12,6Z',
  [D3_NODE_TYPES.TTP]: 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M17.13,17C15.92,18.85 14.11,20.24 12,20.92C9.89,20.24 8.08,18.85 6.87,17C6.53,16.5 6.24,16 6,15.47C6,13.82 8.71,12.47 12,12.47C15.29,12.47 18,13.79 18,15.47C17.76,16 17.47,16.5 17.13,17Z',
  [D3_NODE_TYPES.APT]: 'M16,13C15.71,13 15.38,13 15.03,13.05C16.19,13.89 17,15 17,16.5V19H23V16.5C23,14.17 18.33,13 16,13M8,13C5.67,13 1,14.17 1,16.5V19H15V16.5C15,14.17 10.33,13 8,13M8,11A3,3 0 0,0 11,8A3,3 0 0,0 8,5A3,3 0 0,0 5,8A3,3 0 0,0 8,11M16,11A3,3 0 0,0 19,8A3,3 0 0,0 16,5A3,3 0 0,0 13,8A3,3 0 0,0 16,11Z',
};
/* eslint-enable max-len */

export const DEFAULT_NODE_RADII = {
  [D3_NODE_TYPES.ORG]: 40,
  [D3_NODE_TYPES.ASSET]: 20,
  [D3_NODE_TYPES.TECH]: 18,
  [D3_NODE_TYPES.CVE]: 24,
  [D3_NODE_TYPES.CWE]: 18,
  [D3_NODE_TYPES.CAPEC]: 16,
  [D3_NODE_TYPES.TTP]: 18,
  [D3_NODE_TYPES.APT]: 24,
};

export const DEFAULT_RING_DISTANCES = {
  [D3_NODE_TYPES.ASSET]: 120,
  [D3_NODE_TYPES.TECH]: 240,
  [D3_NODE_TYPES.CVE]: 380,
  [D3_NODE_TYPES.CWE]: 520,
  [D3_NODE_TYPES.CAPEC]: 680,
  [D3_NODE_TYPES.TTP]: 860,
  [D3_NODE_TYPES.APT]: 1060,
};

/**
 * Severity colors for CVE nodes based on CVSS score
 */
export const SEVERITY_COLORS = {
  critical: { light: '#dc2626', dark: '#ef4444' },
  high: { light: '#ea580c', dark: '#f97316' },
  medium: { light: '#ca8a04', dark: '#eab308' },
  low: { light: '#16a34a', dark: '#22c55e' },
  none: { light: '#6b7280', dark: '#9ca3af' },
};

/**
 * Get severity level from CVSS score or baseSeverity string
 */
export const getSeverityLevel = (cvssScore, baseSeverity = null) => {
  // First try CVSS score
  if (cvssScore !== null && cvssScore !== undefined && cvssScore !== '') {
    const score = parseFloat(cvssScore);
    if (!Number.isNaN(score)) {
      if (score >= 9.0) return 'critical';
      if (score >= 7.0) return 'high';
      if (score >= 4.0) return 'medium';
      if (score > 0) return 'low';
      return 'none';
    }
  }
  // Fallback to baseSeverity string
  if (baseSeverity) {
    const severity = String(baseSeverity).toUpperCase();
    if (severity === 'CRITICAL') return 'critical';
    if (severity === 'HIGH') return 'high';
    if (severity === 'MEDIUM') return 'medium';
    if (severity === 'LOW') return 'low';
  }
  return 'none';
};

/**
 * Get severity ring color for a CVE node
 */
export const getSeverityColor = (cvssScore, isDark = false, baseSeverity = null) => {
  const level = getSeverityLevel(cvssScore, baseSeverity);
  return isDark ? SEVERITY_COLORS[level].dark : SEVERITY_COLORS[level].light;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const calculateAngle = (index, total) => (2 * Math.PI * index) / Math.max(1, total) - Math.PI / 2;

export const getAptId = apt => apt.name || apt.APT_group || apt['APT name'];

export const extractTtpId = ttp => {
  if (typeof ttp === 'string') {
    const match = ttp.match(/^(T\d+(?:\.\d+)?)/);
    return match ? match[1] : null;
  }
  return ttp.id || null;
};

export const getNodeColor = (node, theme = LIGHT_THEME) => {
  // APT groups should always use the theme color (consistent with legend)
  // Don't use custom colors from data for APT nodes
  if (node.type === D3_NODE_TYPES.APT) {
    return theme.nodeColors[D3_NODE_TYPES.APT];
  }
  return theme.nodeColors[node.type] || '#999';
};

export const getNodeRadius = (node, customSizes = {}) => (
  customSizes[node.type] || DEFAULT_NODE_RADII[node.type] || 12
);

export const truncateLabel = (label, maxLength) => {
  if (!label || label.length <= maxLength) return label;
  return `${label.substring(0, maxLength - 2)}..`;
};

// ============================================================================
// NODE CREATION FUNCTIONS
// ============================================================================

export const createOrgNode = name => ({
  id: 'org',
  type: D3_NODE_TYPES.ORG,
  label: name,
  x: 0,
  y: 0,
  fx: 0,
  fy: 0,
  data: null,
});

export const createAssetNode = (asset, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: `asset:${asset.id}`,
    type: D3_NODE_TYPES.ASSET,
    label: asset.name,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: asset,
  };
};

export const createTechNode = (tech, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: `tech:${tech.id}`,
    type: D3_NODE_TYPES.TECH,
    label: tech.name,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: tech,
  };
};

export const createCveNode = (cve, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: cve.id,
    type: D3_NODE_TYPES.CVE,
    label: cve.name,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: cve,
  };
};

export const createTtpNode = (ttp, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: ttp.id,
    type: D3_NODE_TYPES.TTP,
    label: ttp.name || ttp.id,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: ttp,
  };
};

export const createAptNode = (apt, index, total, radius) => {
  const angle = calculateAngle(index, total);
  const aptId = getAptId(apt);
  return {
    id: aptId,
    type: D3_NODE_TYPES.APT,
    label: aptId,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: apt,
    color: apt.color,
  };
};

export const createCweNode = (cwe, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: cwe.id,
    type: D3_NODE_TYPES.CWE,
    label: cwe.name || cwe.id,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: cwe,
  };
};

export const createCapecNode = (capec, index, total, radius) => {
  const angle = calculateAngle(index, total);
  return {
    id: capec.id,
    type: D3_NODE_TYPES.CAPEC,
    label: capec.name || capec.id,
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    targetX: Math.cos(angle) * radius,
    targetY: Math.sin(angle) * radius,
    data: capec,
  };
};

// ============================================================================
// LINK CREATION FUNCTIONS
// ============================================================================

export const createLink = (source, target, type) => ({ source, target, type });

export const buildCveTtpLinks = (cves, nodeIds) => {
  const links = [];
  cves.forEach(cve => {
    (cve.ttps || []).forEach(ttpId => {
      if (nodeIds.has(ttpId)) {
        links.push(createLink(cve.id, ttpId, 'cve-ttp'));
      }
    });
  });
  return links;
};

export const buildTtpAptLinks = (aptGroups, nodeIds) => {
  const links = [];
  aptGroups.forEach(apt => {
    const aptId = getAptId(apt);
    const aptTtps = apt.TTPs || apt.ttps || apt.overlap_ttps || [];
    aptTtps.forEach(ttp => {
      const ttpId = extractTtpId(ttp);
      if (ttpId && nodeIds.has(ttpId)) {
        links.push(createLink(ttpId, aptId, 'ttp-apt'));
      }
    });
  });
  return links;
};

// ============================================================================
// GRAPH BUILDING FUNCTIONS
// ============================================================================

export const buildGraph = (data, ringRadii = {}) => {
  const nodes = [];
  const links = [];
  const nodeMap = new Map();

  if (data.organization) {
    const orgNode = createOrgNode(data.organization);
    nodes.push(orgNode);
    nodeMap.set('org', orgNode);
  }

  // Add Asset nodes if present
  const assetRadius = ringRadii.asset || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.ASSET];
  if (data.assets && data.assets.length > 0) {
    data.assets.forEach((asset, i) => {
      const node = createAssetNode(asset, i, data.assets.length, assetRadius);
      nodes.push(node);
      nodeMap.set(node.id, node);
    });
  }

  // Add Technology nodes if present
  const techRadius = ringRadii.tech || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.TECH];
  if (data.technologies && data.technologies.length > 0) {
    data.technologies.forEach((tech, i) => {
      const node = createTechNode(tech, i, data.technologies.length, techRadius);
      nodes.push(node);
      nodeMap.set(node.id, node);
    });
  }

  const cveRadius = ringRadii.cve || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.CVE];
  data.cves.forEach((cve, i) => {
    const node = createCveNode(cve, i, data.cves.length, cveRadius);
    nodes.push(node);
    nodeMap.set(node.id, node);
  });

  // Add CWE nodes if present
  const cweRadius = ringRadii.cwe || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.CWE];
  if (data.cwes && data.cwes.length > 0) {
    data.cwes.forEach((cwe, i) => {
      const node = createCweNode(cwe, i, data.cwes.length, cweRadius);
      nodes.push(node);
      nodeMap.set(node.id, node);
    });
  }

  // Add CAPEC nodes if present
  const capecRadius = ringRadii.capec || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.CAPEC];
  if (data.capecs && data.capecs.length > 0) {
    data.capecs.forEach((capec, i) => {
      const node = createCapecNode(capec, i, data.capecs.length, capecRadius);
      nodes.push(node);
      nodeMap.set(node.id, node);
    });
  }

  const ttpRadius = ringRadii.ttp || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.TTP];
  data.ttps.forEach((ttp, i) => {
    const node = createTtpNode(ttp, i, data.ttps.length, ttpRadius);
    nodes.push(node);
    nodeMap.set(node.id, node);
  });

  const aptRadius = ringRadii.apt || DEFAULT_RING_DISTANCES[D3_NODE_TYPES.APT];
  data.aptGroups.forEach((apt, i) => {
    const node = createAptNode(apt, i, data.aptGroups.length, aptRadius);
    nodes.push(node);
    nodeMap.set(node.id, node);
  });

  // Create links with direct node references (not IDs)
  // Org to Asset links (if assets exist)
  if (data.orgToAssets && data.orgToAssets.length > 0) {
    data.orgToAssets.forEach(assetId => {
      const sourceNode = nodeMap.get('org');
      const targetNode = nodeMap.get(`asset:${assetId}`);
      if (sourceNode && targetNode) {
        links.push({ source: sourceNode, target: targetNode, type: 'org-asset' });
      }
    });
  }

  // Asset to Tech links
  if (data.assetToTechs) {
    Object.entries(data.assetToTechs).forEach(([assetId, techIds]) => {
      const assetNode = nodeMap.get(`asset:${assetId}`);
      if (assetNode) {
        techIds.forEach(techId => {
          const techNode = nodeMap.get(`tech:${techId}`);
          if (techNode) {
            links.push({ source: assetNode, target: techNode, type: 'asset-tech' });
          }
        });
      }
    });
  }

  // Tech to CVE links
  if (data.techToCves) {
    Object.entries(data.techToCves).forEach(([techId, cveIds]) => {
      const techNode = nodeMap.get(`tech:${techId}`);
      if (techNode) {
        cveIds.forEach(cveId => {
          const cveNode = nodeMap.get(cveId);
          if (cveNode) {
            links.push({ source: techNode, target: cveNode, type: 'tech-cve' });
          }
        });
      }
    });
  }

  // Org to CVE links (fallback when no assets)
  if (!data.assets || data.assets.length === 0) {
    data.cves.forEach(cve => {
      const sourceNode = nodeMap.get('org');
      const targetNode = nodeMap.get(cve.id);
      if (sourceNode && targetNode) {
        links.push({ source: sourceNode, target: targetNode, type: 'org-cve' });
      }
    });
  }

  // CVE to CWE links
  if (data.cveToWeaknesses) {
    Object.entries(data.cveToWeaknesses).forEach(([cveId, cweIds]) => {
      const cveNode = nodeMap.get(cveId);
      if (cveNode) {
        cweIds.forEach(cweId2 => {
          const cweNode = nodeMap.get(cweId2);
          if (cweNode) {
            links.push({ source: cveNode, target: cweNode, type: 'cve-cwe' });
          }
        });
      }
    });
  }

  // CWE to CAPEC links
  if (data.cweToCapecs) {
    Object.entries(data.cweToCapecs).forEach(([cweId, capecIds]) => {
      const cweNode = nodeMap.get(cweId);
      if (cweNode) {
        capecIds.forEach(capecId => {
          const capecNode = nodeMap.get(capecId);
          if (capecNode) {
            links.push({ source: cweNode, target: capecNode, type: 'cwe-capec' });
          }
        });
      }
    });
  }

  // CAPEC to TTP links
  if (data.capecToTtps) {
    Object.entries(data.capecToTtps).forEach(([capecId, ttpIds]) => {
      const capecNode = nodeMap.get(capecId);
      if (capecNode) {
        ttpIds.forEach(ttpId => {
          const ttpNode = nodeMap.get(ttpId);
          if (ttpNode) {
            links.push({ source: capecNode, target: ttpNode, type: 'capec-ttp' });
          }
        });
      }
    });
  }

  // CVE to TTP links (direct, for backward compatibility or when no CWE/CAPEC)
  data.cves.forEach(cve => {
    (cve.ttps || []).forEach(ttpId => {
      const sourceNode = nodeMap.get(cve.id);
      const targetNode = nodeMap.get(ttpId);
      if (sourceNode && targetNode) {
        // Only add if not already connected via CAPEC
        const hasCapecPath = data.capecs && data.capecs.length > 0;
        if (!hasCapecPath) {
          links.push({ source: sourceNode, target: targetNode, type: 'cve-ttp' });
        }
      }
    });
  });

  // TTP to APT links
  data.aptGroups.forEach(apt => {
    const aptId = getAptId(apt);
    const aptNode = nodeMap.get(aptId);
    const aptTtps = apt.TTPs || apt.ttps || apt.overlap_ttps || [];
    aptTtps.forEach(ttp => {
      const ttpId = extractTtpId(ttp);
      const ttpNode = nodeMap.get(ttpId);
      if (ttpNode && aptNode) {
        links.push({ source: ttpNode, target: aptNode, type: 'ttp-apt' });
      }
    });
  });

  return { nodes, links };
};

// ============================================================================
// D3 RENDERING FUNCTIONS - No simulation, static positioning
// ============================================================================

export const createZoomBehavior = onZoom => d3.zoom()
  .scaleExtent([0.3, 3])
  .on('zoom', onZoom);

export const generateLinkPath = d => {
  const dx = d.target.x - d.source.x;
  const dy = d.target.y - d.source.y;
  const dr = Math.sqrt(dx * dx + dy * dy) * 0.8;
  return `M${d.source.x},${d.source.y}A${dr},${dr} 0 0,1 ${d.target.x},${d.target.y}`;
};

// ============================================================================
// CONNECTION HIGHLIGHTING FUNCTIONS
// ============================================================================

/**
 * Get all connected node IDs by traversing the full path in both directions
 * This will highlight the complete chain: APT -> TTP -> CAPEC -> CWE -> CVE -> Org
 */
export const getConnectedNodeIds = (nodeId, links) => {
  const connected = new Set([nodeId]);
  const visited = new Set([nodeId]);

  // Traverse forward (source -> target)
  const traverseForward = id => {
    links.forEach(link => {
      const sourceId = link.source.id || link.source;
      const targetId = link.target.id || link.target;
      if (sourceId === id && !visited.has(targetId)) {
        visited.add(targetId);
        connected.add(targetId);
        traverseForward(targetId);
      }
    });
  };

  // Traverse backward (target -> source)
  const traverseBackward = id => {
    links.forEach(link => {
      const sourceId = link.source.id || link.source;
      const targetId = link.target.id || link.target;
      if (targetId === id && !visited.has(sourceId)) {
        visited.add(sourceId);
        connected.add(sourceId);
        traverseBackward(sourceId);
      }
    });
  };

  traverseForward(nodeId);
  traverseBackward(nodeId);

  return connected;
};

/**
 * Check if a link is part of the connected path
 */
export const isLinkConnected = (link, nodeId, connectedIds) => {
  const sourceId = link.source.id || link.source;
  const targetId = link.target.id || link.target;
  // Link is connected if both source and target are in the connected set
  if (connectedIds) {
    return connectedIds.has(sourceId) && connectedIds.has(targetId);
  }
  // Fallback: direct connection only
  return sourceId === nodeId || targetId === nodeId;
};

/**
 * Count connections for each node
 * Returns a Map of nodeId -> connection count
 */
export const countNodeConnections = links => {
  const counts = new Map();

  links.forEach(link => {
    const sourceId = link.source.id || link.source;
    const targetId = link.target.id || link.target;

    counts.set(sourceId, (counts.get(sourceId) || 0) + 1);
    counts.set(targetId, (counts.get(targetId) || 0) + 1);
  });

  return counts;
};

/**
 * Calculate dynamic node radius based on connection count
 * More connections = larger node (within limits)
 * @param {Object} node - The node object
 * @param {Object} customSizes - Custom base sizes per type
 * @param {Map} connectionCounts - Map of nodeId -> connection count
 * @param {number} maxConnections - Maximum connections in the graph (for scaling)
 * @returns {number} - The calculated radius
 */
export const getDynamicNodeRadius = (node, customSizes, connectionCounts, maxConnections) => {
  const baseRadius = customSizes[node.type] || DEFAULT_NODE_RADII[node.type] || 12;

  // Org node doesn't scale
  if (node.type === D3_NODE_TYPES.ORG) {
    return baseRadius;
  }

  const connections = connectionCounts?.get(node.id) || 1;

  // Scale factor: nodes with more connections get up to 100% larger
  // Use square root scaling for more visible differences
  const maxScale = 2.0;
  const minScale = 0.7;

  if (!maxConnections || maxConnections <= 1) {
    return baseRadius;
  }

  // Square root scaling for more visible differences
  const normalizedConnections = Math.sqrt(connections) / Math.sqrt(maxConnections);
  const scale = minScale + (maxScale - minScale) * normalizedConnections;

  return Math.round(baseRadius * scale);
};

// ============================================================================
// DRAG BEHAVIOR - Simple drag, nodes stay where you put them
// Tracks drag state and calls onClick if no drag occurred
// ============================================================================

export const createDragBehavior = (onDrag, onClick) => d3.drag()
  .on('start', function onStart(event, d) {
    d3.select(this).raise().style('cursor', 'grabbing');
    // Store initial position on the data object for this drag session
    // eslint-disable-next-line no-param-reassign
    d.dragStartX = event.x;
    // eslint-disable-next-line no-param-reassign
    d.dragStartY = event.y;
    // eslint-disable-next-line no-param-reassign
    d.dragMoved = false; // Use different name to avoid conflicts
  })
  .on('drag', (event, d) => {
    if (d.type === D3_NODE_TYPES.ORG) return;
    // Only mark as moved and update position if moved more than 5 pixels
    const dx = Math.abs(event.x - d.dragStartX);
    const dy = Math.abs(event.y - d.dragStartY);
    if (dx > 5 || dy > 5) {
      // eslint-disable-next-line no-param-reassign
      d.dragMoved = true;
      // eslint-disable-next-line no-param-reassign
      d.x = event.x;
      // eslint-disable-next-line no-param-reassign
      d.y = event.y;
      onDrag();
    }
  })
  .on('end', function onEnd(event, d) {
    d3.select(this).style('cursor', 'grab');
    // If no significant movement occurred, treat as click
    const wasDragged = d.dragMoved === true;
    // Reset for next interaction
    // eslint-disable-next-line no-param-reassign
    d.dragMoved = false;
    if (!wasDragged && onClick) {
      onClick(event, d);
    }
  });
